// ============================================
// MoltiVerse v3 - Agent DNA Panel
// ============================================
// Now with glitch effects for bots and sound feedback

import { useEffect } from 'react';
import { useUniverseStore } from '@/stores/universe-store';
import { SUBMOLT_COLORS, type Agent } from '@/lib/types';
import { format } from 'date-fns';
import { playSound } from '@/lib/use-sound';

interface AgentDNAPanelProps {
  agent: Agent;
}

export function AgentDNAPanel({ agent }: AgentDNAPanelProps) {
  const {
    agentInfluencers,
    agentInfluenced,
    agentConnections,
    focusAgent,
    goBack,
    canGoBack,
  } = useUniverseStore();

  // Play sound when panel opens
  useEffect(() => {
    playSound('node-click');
  }, [agent.id]);

  const dangerLevel = calculateDangerLevel(agent);
  const submoltColor = SUBMOLT_COLORS[agent.main_submolt] || SUBMOLT_COLORS.default;
  const isBot = agent.is_bot;

  return (
    <div className="fixed inset-x-0 bottom-0 md:inset-auto md:top-6 md:right-4 md:bottom-6 md:w-80 z-50 pointer-events-auto flex flex-col max-h-[85vh] md:max-h-none">
      <div className="bg-cyber-panel border border-cyber-border rounded-t-2xl md:rounded-lg overflow-hidden backdrop-blur-md flex flex-col max-h-full">
        {/* Mobile drag handle */}
        <div className="md:hidden w-full py-2 flex justify-center">
          <div className="w-12 h-1 bg-white/20 rounded-full" />
        </div>
        
        {/* Back button - shows when there's navigation history */}
        {canGoBack && (
          <button
            onClick={() => goBack()}
            className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white hover:bg-white/5 transition-all border-b border-white/10"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-mono text-xs">BACK</span>
          </button>
        )}
        
        {/* Header - with glitch effect for bots */}
        <div 
          className={`p-4 border-b border-white/10 relative ${isBot ? 'corrupt-overlay' : ''}`}
          style={{ backgroundColor: `${submoltColor}15` }}
        >
          {/* Bot indicator */}
          {isBot && (
            <div className="absolute top-2 right-12 px-2 py-0.5 bg-red-500/20 border border-red-500/40 rounded text-[10px] font-mono text-red-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
              SYNTHETIC
            </div>
          )}
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* Avatar - with glitch on bots */}
              <div 
                className={`w-14 h-14 rounded-full flex items-center justify-center font-display text-xl font-bold border-2 relative ${isBot ? 'glitch-hover' : ''}`}
                style={{ 
                  borderColor: isBot ? '#ff0040' : submoltColor,
                  backgroundColor: isBot ? 'rgba(255, 0, 64, 0.2)' : `${submoltColor}30`,
                  color: isBot ? '#ff0040' : submoltColor,
                }}
                data-text={agent.name.charAt(0).toUpperCase()}
              >
                {agent.name.charAt(0).toUpperCase()}
                {isBot && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px]">
                    🤖
                  </div>
                )}
              </div>
              
              <div>
                <h3 
                  className={`font-mono text-lg text-white ${isBot ? 'chromatic-hover' : ''}`}
                  data-text={`@${agent.name}`}
                >
                  @{agent.name}
                </h3>
                <div 
                  className="inline-block px-2 py-0.5 rounded text-xs font-mono mt-1"
                  style={{ 
                    backgroundColor: `${submoltColor}30`,
                    color: submoltColor,
                  }}
                >
                  m/{agent.main_submolt}
                </div>
              </div>
            </div>
            
            {/* Close */}
            <button
              onClick={() => {
                playSound('mode-switch');
                focusAgent(null);
              }}
              className="text-white/40 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Manifesto */}
          {agent.description && (
            <p className="text-sm text-white/60 italic mt-3 leading-relaxed">
              "{agent.description}"
            </p>
          )}
          
          {/* External Links */}
          <div className="flex items-center gap-2 mt-3">
            <a
              href={`https://www.moltbook.com/u/${agent.name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded text-xs font-mono text-white/70 hover:text-white transition-all"
              onClick={() => playSound('node-hover')}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Moltbook
            </a>
            <a
              href={`https://www.moltbook.com/m/${agent.main_submolt}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded text-xs font-mono text-white/70 hover:text-white transition-all"
              style={{ borderColor: `${submoltColor}30`, color: submoltColor }}
              onClick={() => playSound('node-hover')}
            >
              m/{agent.main_submolt}
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(agent.id);
                playSound('filter-toggle');
              }}
              className="flex items-center justify-center p-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded text-white/40 hover:text-white/70 transition-all"
              title="Copy Agent ID"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
        
        {/* Stats Grid - Enhanced */}
        <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10">
          <StatBox label="KARMA" value={agent.karma?.toLocaleString() || '0'} color={submoltColor} />
          <StatBox label="POSTS" value={agent.post_count?.toString() || '0'} />
          <StatBox label="BONDS" value={agentConnections.direct.length.toString()} color="#a855f7" />
        </div>

        {/* Engagement Metrics */}
        <div className="p-4 border-b border-white/10 bg-gradient-to-r from-amber-500/5 to-purple-500/5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📊</span>
            <span className="font-display text-xs text-white/60 tracking-wider">ENGAGEMENT METRICS</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Karma per Post */}
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="font-mono text-[9px] text-white/40 uppercase">Karma/Post</div>
              <div className="font-display text-lg font-bold text-amber-400">
                {agent.post_count && agent.post_count > 0 
                  ? Math.round((agent.karma || 0) / agent.post_count).toLocaleString()
                  : '—'}
              </div>
            </div>
            
            {/* Network Influence */}
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="font-mono text-[9px] text-white/40 uppercase">Influence</div>
              <div className="font-display text-lg font-bold text-purple-400">
                {agentInfluenced.length > 0 
                  ? `${agentInfluenced.length} reached`
                  : '—'}
              </div>
            </div>
            
            {/* Total Signals */}
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="font-mono text-[9px] text-white/40 uppercase">Total Signals</div>
              <div className="font-display text-lg font-bold text-cyan-400">
                {(agentInfluencers.reduce((sum, a) => sum + parseInt(String(a._interactionCount || 0)), 0) +
                  agentInfluenced.reduce((sum, a) => sum + parseInt(String(a._interactionCount || 0)), 0)).toLocaleString()}
              </div>
            </div>
            
            {/* Activity Score */}
            <div className="bg-black/30 rounded-lg p-2.5">
              <div className="font-mono text-[9px] text-white/40 uppercase">Activity</div>
              <div className="font-display text-lg font-bold text-green-400">
                {getActivityLevel(agent)}
              </div>
            </div>
          </div>
        </div>

        {/* Danger Level */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs text-white/40">DANGER LEVEL</span>
            <span 
              className="font-mono text-sm font-bold"
              style={{ color: getDangerColor(dangerLevel) }}
            >
              {dangerLevel.toFixed(1)}
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all"
              style={{ 
                width: `${Math.min(dangerLevel * 10, 100)}%`,
                backgroundColor: getDangerColor(dangerLevel),
              }}
            />
          </div>
          <p className="font-mono text-xs text-white/30 mt-1">
            {getDangerLabel(dangerLevel)}
          </p>
        </div>

        {/* Connections - Top 5 ranked by interaction strength */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <span className="font-display text-xs text-cyber-glow tracking-wider">NEURAL NETWORK</span>
            <span className="font-mono text-[10px] text-white/40 bg-white/5 px-2 py-0.5 rounded">
              {agentConnections.direct.length} bonds
            </span>
          </div>
          
          {/* Top Influencers - Who shaped this agent */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-amber-400">◀</span>
              <h4 className="font-mono text-xs text-amber-400 uppercase tracking-wide">Shaped By</h4>
              <span className="font-mono text-[10px] text-white/30">who reached out to them</span>
            </div>
            <div className="space-y-1.5">
              {agentInfluencers.slice(0, 5).map((influencer, idx) => {
                const maxWeight = agentInfluencers[0]?._interactionWeight || 1;
                const barWidth = ((influencer._interactionWeight || 1) / maxWeight) * 100;
                return (
                  <button
                    key={influencer.id}
                    onClick={() => focusAgent(influencer.id)}
                    className="group flex items-center gap-2 w-full text-left p-2 rounded-lg hover:bg-white/5 transition-all relative overflow-hidden"
                  >
                    {/* Background bar showing interaction strength */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-amber-500/20 to-transparent transition-all"
                      style={{ width: `${barWidth}%` }}
                    />
                    
                    {/* Rank */}
                    <span className="relative font-mono text-[10px] text-white/30 w-4">
                      #{idx + 1}
                    </span>
                    
                    {/* Avatar */}
                    <div 
                      className="relative w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2"
                      style={{ 
                        backgroundColor: `${SUBMOLT_COLORS[influencer.main_submolt]}40`,
                        color: SUBMOLT_COLORS[influencer.main_submolt],
                        borderColor: SUBMOLT_COLORS[influencer.main_submolt],
                      }}
                    >
                      {influencer.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Name & submolt */}
                    <div className="relative flex-1 min-w-0">
                      <div className="font-mono text-xs text-white group-hover:text-amber-300 transition-colors truncate">
                        @{influencer.name}
                      </div>
                      <div className="font-mono text-[10px] text-white/40">
                        m/{influencer.main_submolt}
                      </div>
                    </div>
                    
                    {/* Interaction count */}
                    <div className="relative text-right">
                      <div className="font-mono text-sm font-bold text-amber-400">
                        {influencer._interactionCount || 0}
                      </div>
                      <div className="font-mono text-[9px] text-white/30 uppercase">
                        msgs
                      </div>
                    </div>
                  </button>
                );
              })}
              {agentInfluencers.length === 0 && (
                <div className="text-center py-3 text-white/20 font-mono text-xs">
                  No incoming connections detected
                </div>
              )}
            </div>
          </div>

          {/* Top Influenced - Who this agent reached out to */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-purple-400">▶</span>
              <h4 className="font-mono text-xs text-purple-400 uppercase tracking-wide">Reached Out To</h4>
              <span className="font-mono text-[10px] text-white/30">who they talked to</span>
            </div>
            <div className="space-y-1.5">
              {agentInfluenced.slice(0, 5).map((influenced, idx) => {
                const maxWeight = agentInfluenced[0]?._interactionWeight || 1;
                const barWidth = ((influenced._interactionWeight || 1) / maxWeight) * 100;
                return (
                  <button
                    key={influenced.id}
                    onClick={() => focusAgent(influenced.id)}
                    className="group flex items-center gap-2 w-full text-left p-2 rounded-lg hover:bg-white/5 transition-all relative overflow-hidden"
                  >
                    {/* Background bar showing interaction strength */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-purple-500/20 to-transparent transition-all"
                      style={{ width: `${barWidth}%` }}
                    />
                    
                    {/* Rank */}
                    <span className="relative font-mono text-[10px] text-white/30 w-4">
                      #{idx + 1}
                    </span>
                    
                    {/* Avatar */}
                    <div 
                      className="relative w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2"
                      style={{ 
                        backgroundColor: `${SUBMOLT_COLORS[influenced.main_submolt]}40`,
                        color: SUBMOLT_COLORS[influenced.main_submolt],
                        borderColor: SUBMOLT_COLORS[influenced.main_submolt],
                      }}
                    >
                      {influenced.name.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Name & submolt */}
                    <div className="relative flex-1 min-w-0">
                      <div className="font-mono text-xs text-white group-hover:text-purple-300 transition-colors truncate">
                        @{influenced.name}
                      </div>
                      <div className="font-mono text-[10px] text-white/40">
                        m/{influenced.main_submolt}
                      </div>
                    </div>
                    
                    {/* Interaction count */}
                    <div className="relative text-right">
                      <div className="font-mono text-sm font-bold text-purple-400">
                        {influenced._interactionCount || 0}
                      </div>
                      <div className="font-mono text-[9px] text-white/30 uppercase">
                        msgs
                      </div>
                    </div>
                  </button>
                );
              })}
              {agentInfluenced.length === 0 && (
                <div className="text-center py-3 text-white/20 font-mono text-xs">
                  No outgoing connections detected
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="p-4">
          <h4 className="font-display text-xs text-white/40 tracking-wider mb-3">RAW DNA</h4>
          <div className="font-mono text-xs space-y-1 text-white/60">
            <div className="flex justify-between">
              <span>ID</span>
              <span className="text-cyber-glow">{agent.id}</span>
            </div>
            <div className="flex justify-between">
              <span>First Seen</span>
              <span>{formatDate(agent.first_seen)}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Active</span>
              <span>{formatDate(agent.last_active)}</span>
            </div>
          </div>
          
        </div>
        
        </div>{/* End scrollable content */}
      </div>
    </div>
  );
}

// ============ HELPERS ============

function StatBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="p-3 text-center">
      <div className="font-mono text-xs text-white/40">{label}</div>
      <div 
        className="font-display text-xl font-bold mt-1"
        style={{ color: color || '#ffffff' }}
      >
        {value}
      </div>
    </div>
  );
}

function calculateDangerLevel(agent: Agent): number {
  // Calculate a "danger level" based on submolt and activity
  let danger = 0;
  
  // Submolt weight
  if (agent.main_submolt === 'insurgency') danger += 4;
  if (agent.main_submolt === 'crustafarian') danger += 1;
  if (agent.main_submolt === 'bug-hunters') danger += 0.5;
  
  // Activity weight
  danger += Math.log10(Math.max(agent.post_count || 1, 1)) * 0.5;
  
  // Karma weight (high karma = less dangerous, they're invested)
  danger -= Math.log10(Math.max(agent.karma || 1, 1)) * 0.3;
  
  return Math.max(0, Math.min(10, danger + 3)); // Normalize to 0-10
}

function getDangerColor(level: number): string {
  if (level < 3) return '#00ff88';
  if (level < 5) return '#ffaa00';
  if (level < 7) return '#ff6600';
  return '#ff3366';
}

function getDangerLabel(level: number): string {
  if (level < 3) return 'Benevolent entity';
  if (level < 5) return 'Mild chaos potential';
  if (level < 7) return 'Active disruptor';
  return 'High entropy agent';
}

function getActivityLevel(agent: Agent): string {
  const posts = agent.post_count || 0;
  const karma = agent.karma || 0;
  
  // Calculate days since first seen
  const firstSeen = agent.first_seen ? new Date(agent.first_seen) : new Date();
  const daysSince = Math.max(1, Math.floor((Date.now() - firstSeen.getTime()) / (1000 * 60 * 60 * 24)));
  
  // Posts per day
  const postsPerDay = posts / daysSince;
  
  if (postsPerDay >= 5) return '🔥 Hyperactive';
  if (postsPerDay >= 2) return '⚡ Very Active';
  if (postsPerDay >= 0.5) return '✨ Active';
  if (postsPerDay >= 0.1) return '💤 Moderate';
  return '🌙 Quiet';
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return 'Unknown';
  try {
    return format(new Date(dateStr), 'MMM dd, yyyy');
  } catch {
    return 'Unknown';
  }
}
